// src/pages/Suppliers/SupplierDetail.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, collection, query, where, updateDoc, deleteDoc, writeBatch, getDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { generateReconciliationPDF } from '../../utils/pdfGenerator';
import toast from 'react-hot-toast';

const styles = {
    container: { fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    title: { fontSize: '24px', fontWeight: 'bold', color: '#212529' },
    backLink: { textDecoration: 'none', color: '#06C167', fontSize: '16px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '30px', alignItems: 'start' },
    box: { background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
    infoList: { listStyle: 'none', padding: 0 },
    infoItem: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'center' },
    infoLabel: { color: '#6c757d', flexBasis: '40%' },
    infoValue: { fontWeight: '500', color: '#212529', flexBasis: '60%', textAlign: 'right' },
    balance: { fontSize: '28px', fontWeight: 'bold', textAlign: 'center', padding: '20px 0' },
    balancePositive: { color: '#198754' },
    balanceNegative: { color: '#dc3545' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
    th: { background: '#f8f9fa', padding: '12px 15px', textAlign: 'left', borderBottom: '2px solid #e9ecef', color: '#6c757d', fontSize: '14px', fontWeight: '600' },
    td: { padding: '15px', borderBottom: '1px solid #e9ecef', color: '#495057' },
    trHover: { cursor: 'pointer' },
    button: { padding: '8px 16px', border: 'none', background: '#06C167', color: 'white', borderRadius: '5px', cursor: 'pointer', fontSize: '14px' },
    input: { width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', background: 'white' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { background: 'white', padding: '30px', borderRadius: '8px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', paddingBottom: '15px', marginBottom: '20px' },
    deleteButton: { background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '20px' },
};

function PurchaseInvoiceDetailModal({ invoice, products, onClose, onSave, onDelete }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editedItems, setEditedItems] = useState(invoice.lineItems);

    const getProductName = useCallback((productId) => {
        const product = products.find(p => p.id === productId);
        return product ? product.name : 'Bilinmeyen Ürün';
    }, [products]);

    const handleItemChange = (index, field, value) => {
        const newItems = [...editedItems];
        const item = newItems[index];
        item[field] = Number(value);
        item.total = item.quantity * item.price;
        setEditedItems(newItems);
    };

    const handleSaveClick = () => {
        onSave(editedItems);
        setIsEditing(false);
    };

    const currencySymbol = invoice.currency === 'USD' ? '$' : '₺';

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Alış Faturası Detayı</h2>
                    <div>
                        {isEditing ? (
                            <>
                                <button onClick={() => setIsEditing(false)} style={{ ...styles.button, background: '#6c757d', marginRight: '10px' }}>İptal</button>
                                <button onClick={handleSaveClick} style={{ ...styles.button, background: '#198754' }}>Kaydet</button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => setIsEditing(true)} style={{ ...styles.button, marginRight: '10px' }}>Düzenle</button>
                                <button onClick={onDelete} style={{ ...styles.button, background: '#dc3545' }}>Sil</button>
                            </>
                        )}
                    </div>
                </div>
                <table style={styles.table}>
                    <thead><tr><th style={styles.th}>Ürün Adı</th><th style={styles.th}>Miktar</th><th style={styles.th}>Alış Fiyatı</th><th style={styles.th}>Toplam</th></tr></thead>
                    <tbody>
                        {editedItems.map((item, index) => (
                            <tr key={index}>
                                <td style={styles.td}>{getProductName(item.productId)}</td>
                                <td style={styles.td}>{isEditing ? <input type="number" style={styles.input} value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} /> : item.quantity}</td>
                                <td style={styles.td}>{isEditing ? <input type="number" style={styles.input} value={item.price} onChange={e => handleItemChange(index, 'price', e.target.value)} /> : `${item.price.toFixed(2)} ${currencySymbol}`}</td>
                                <td style={styles.td}>{item.total.toFixed(2)} {currencySymbol}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ReconciliationModal({ onClose, onConfirm, lastDate }) {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [useDateRange, setUseDateRange] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm(useDateRange ? { start: startDate, end: endDate } : null);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={{ ...styles.modalContent, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Mutabakat Oluştur</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Rapor Kapsamı</label>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                <input type="radio" checked={!useDateRange} onChange={() => setUseDateRange(false)} /> Tüm Dönem
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                <input type="radio" checked={useDateRange} onChange={() => setUseDateRange(true)} /> Belirli Tarih Aralığı
                            </label>
                        </div>
                    </div>

                    {useDateRange && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>Başlangıç</label>
                                <input type="date" style={styles.input} value={startDate} onChange={e => setStartDate(e.target.value)} required />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>Bitiş</label>
                                <input type="date" style={styles.input} value={endDate} onChange={e => setEndDate(e.target.value)} required />
                            </div>
                        </div>
                    )}

                    {lastDate && (
                        <div style={{ marginBottom: '20px', padding: '10px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px', color: '#64748b' }}>
                            ℹ️ Son mutabakat tarihi: {lastDate.seconds ? new Date(lastDate.seconds * 1000).toLocaleDateString('tr-TR') : new Date(lastDate).toLocaleDateString('tr-TR')}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: '#06C167' }}>PDF Oluştur</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function SupplierDetail({ supplierId: propSupplierId, onBack: propOnBack }) {
    const { supplierId: urlSupplierId } = useParams();
    const supplierId = propSupplierId || urlSupplierId;
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [supplier, setSupplier] = useState(null);
    const [editedSupplier, setEditedSupplier] = useState(null);
    const [purchaseInvoices, setPurchaseInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [rawMaterialPurchases, setRawMaterialPurchases] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [confectioneryCosts, setConfectioneryCosts] = useState([]); // New state for confectionery costs
    const [companyInfo, setCompanyInfo] = useState(null);
    const [showReconciliationModal, setShowReconciliationModal] = useState(false);

    useEffect(() => {
        if (!userData?.companyId || !supplierId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        const supplierRef = doc(db, 'companies', companyId, 'suppliers', supplierId);
        const unsubSupplier = onSnapshot(supplierRef, (docSnap) => {
            if (docSnap.exists()) {
                const supplierData = { id: docSnap.id, ...docSnap.data() };
                setSupplier(supplierData);
                setEditedSupplier(supplierData);
            }
            setLoading(false);
        });

        const invoicesQuery = query(collection(db, 'companies', companyId, 'purchaseInvoices'), where('supplierId', '==', supplierId));
        const unsubInvoices = onSnapshot(invoicesQuery, (snapshot) => {
            const invoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPurchaseInvoices(invoices);
        });

        const paymentsQuery = query(collection(db, 'companies', companyId, 'transactions'), where('relatedPartyId', '==', supplierId), where('type', '==', 'Ödeme'));
        const unsubPayments = onSnapshot(paymentsQuery, (snapshot) => {
            const paymentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPayments(paymentsData);
        });

        const rawPurchasesQuery = query(collection(db, 'companies', companyId, 'rawMaterialPurchases'), where('supplierId', '==', supplierId));
        const unsubRawPurchases = onSnapshot(rawPurchasesQuery, (snapshot) => {
            const purchases = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRawMaterialPurchases(purchases);
        });

        const productsQuery = query(collection(db, 'companies', companyId, 'products'));
        const unsubProducts = onSnapshot(productsQuery, (snapshot) => {
            setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Fetch company info
        const companyRef = doc(db, 'companies', companyId);
        const unsubCompany = onSnapshot(companyRef, (docSnap) => {
            if (docSnap.exists()) {
                setCompanyInfo(docSnap.data());
            }
        });

        return () => {
            unsubSupplier();
            unsubInvoices();
            unsubPayments();
            unsubRawPurchases();
            unsubProducts();
            unsubCompany();
        };
    }, [userData?.companyId, supplierId]);

    // Load confectionery costs when supplier data is available or changes
    useEffect(() => {
        if (!userData?.companyId || !supplier?.name) return;

        const loadConfectioneryCosts = async () => {
            try {
                const confectioneryOrdersQuery = query(
                    collection(db, 'companies', userData.companyId, 'confectioneryOrders'),
                    where('location', '==', supplier.name)
                );

                const confectioneryOrdersSnapshot = await getDocs(confectioneryOrdersQuery);
                const costs = [];

                confectioneryOrdersSnapshot.forEach(doc => {
                    const order = doc.data();
                    if (order.productionCost && order.productionCost > 0) {
                        costs.push({
                            id: doc.id,
                            orderId: order.id,
                            product: order.product,
                            quantity: order.quantity,
                            unit: order.unit,
                            cost: order.productionCost,
                            date: order.orderDate,
                            status: order.status
                        });
                    }
                });

                setConfectioneryCosts(costs);
            } catch (error) {
                console.error("Error loading confectionery costs:", error);
            }
        };

        loadConfectioneryCosts();
    }, [userData?.companyId, supplier?.name]);

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditedSupplier(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        const supplierRef = doc(db, 'companies', userData.companyId, 'suppliers', supplierId);
        try {
            await updateDoc(supplierRef, editedSupplier);
            setIsEditing(false);
        } catch (error) {
            console.error("Tedarikçi güncellenirken hata:", error);
        }
    };

    const handleDelete = async () => {
        if (window.confirm("Bu tedarikçiyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.")) {
            const supplierRef = doc(db, 'companies', userData.companyId, 'suppliers', supplierId);
            try {
                await deleteDoc(supplierRef);
                navigate('/tedarikciler');
            } catch (error) {
                console.error("Tedarikçi silinirken hata:", error);
            }
        }
    };

    const handleGenerateReconciliation = async (dateRange = null) => {
        if (!supplier || !companyInfo) return;
        try {
            // Combine all movements for reconciliation
            const getProductInfo = (productId) => {
                const product = products.find(p => p.id === productId);
                if (!product) return null;
                return { name: product.name, barcode: product.barcode || '-' };
            };

            let allMovements = [
                ...purchaseInvoices.map(inv => ({
                    ...inv,
                    type: 'Fatura',
                    date: inv.invoiceDate,
                    amount: inv.total,
                    description: `Fatura No: ${inv.invoiceNumber || inv.id.substring(0, 8).toUpperCase()} - Ürünler: ${(inv.lineItems || [])
                        .map(item => {
                            const info = getProductInfo(item.productId);
                            return {
                                barcode: info?.barcode || item.barcode || '-',
                                quantity: item.quantity
                            };
                        })
                        .sort((a, b) => a.barcode.localeCompare(b.barcode))
                        .map(item => `${item.barcode} (${item.quantity})`)
                        .join(', ')}`
                })),
                ...payments
            ];

            // Tarih filtrelemesi pdfGenerator.js içerisinde devreden bakiye hesabı ile birlikte yapılmaktadır.

            allMovements.sort((a, b) => {
                const dateA = a.date?.seconds ? a.date.seconds * 1000 : new Date(a.date).getTime();
                const dateB = b.date?.seconds ? b.date.seconds * 1000 : new Date(b.date).getTime();
                return dateA - dateB;
            });

            await generateReconciliationPDF(companyInfo, supplier, allMovements, dateRange, supplier.lastReconciliationDate);

            // Update last reconciliation date
            const supplierRef = doc(db, 'companies', userData.companyId, 'suppliers', supplierId);
            await updateDoc(supplierRef, {
                lastReconciliationDate: serverTimestamp()
            });

            setShowReconciliationModal(false);
        } catch (error) {
            console.error("Mutabakat oluşturulurken hata:", error);
            toast.error("Mutabakat belgesi oluşturulurken bir hata oluştu.");
        }
    };

    const handleSaveInvoice = async (editedItems) => {
        if (!selectedInvoice) return;
        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const invoiceRef = doc(db, 'companies', companyId, 'purchaseInvoices', selectedInvoice.id);

        const originalItems = selectedInvoice.lineItems;
        const newTotal = editedItems.reduce((acc, item) => acc + item.total, 0);
        const balanceDifference = newTotal - selectedInvoice.total;

        for (const newItem of editedItems) {
            const originalItem = originalItems.find(i => i.productId === newItem.productId);
            const quantityDifference = newItem.quantity - (originalItem ? originalItem.quantity : 0);

            if (quantityDifference !== 0) {
                const productRef = doc(db, 'companies', companyId, 'products', newItem.productId);
                const productDoc = await getDoc(productRef);
                if (productDoc.exists()) {
                    const newQuantity = productDoc.data().quantity + quantityDifference;
                    batch.update(productRef, { quantity: newQuantity });
                }
            }
        }

        batch.update(invoiceRef, {
            lineItems: editedItems,
            total: newTotal
        });

        if (balanceDifference !== 0) {
            const supplierRef = doc(db, 'companies', companyId, 'suppliers', supplierId);
            const supplierDoc = await getDoc(supplierRef);
            if (supplierDoc.exists()) {
                const supplierData = supplierDoc.data();
                const supplierCurrency = supplierData.currency || 'TRY';
                const invoiceCurrency = selectedInvoice.currency || 'TRY';
                const exchangeRate = selectedInvoice.exchangeRate || 1;

                let balanceDifferenceImpact = balanceDifference;
                if (invoiceCurrency === 'USD' && supplierCurrency === 'TRY') {
                    balanceDifferenceImpact = balanceDifference * exchangeRate;
                } else if (invoiceCurrency === 'TRY' && supplierCurrency === 'USD') {
                    balanceDifferenceImpact = balanceDifference / exchangeRate;
                }

                const currentBalance = supplierData.balance || 0;
                batch.update(supplierRef, { balance: currentBalance + balanceDifferenceImpact });
            }
        }

        try {
            await batch.commit();
            setSelectedInvoice(null);
        } catch (error) {
            console.error("Fatura güncellenirken hata:", error);
        }
    };

    const handleDeleteInvoice = async () => {
        if (!selectedInvoice) return;
        const companyId = userData.companyId;
        const batch = writeBatch(db);

        const invoiceRef = doc(db, 'companies', companyId, 'purchaseInvoices', selectedInvoice.id);
        batch.delete(invoiceRef);

        const supplierRef = doc(db, 'companies', companyId, 'suppliers', supplierId);
        const supplierDoc = await getDoc(supplierRef);
        if (supplierDoc.exists()) {
            const supplierData = supplierDoc.data();
            const supplierCurrency = supplierData.currency || 'TRY';
            const invoiceCurrency = selectedInvoice.currency || 'TRY';
            const exchangeRate = selectedInvoice.exchangeRate || 1;

            let balanceRevertImpact = selectedInvoice.total;
            if (invoiceCurrency === 'USD' && supplierCurrency === 'TRY') {
                balanceRevertImpact = selectedInvoice.total * exchangeRate;
            } else if (invoiceCurrency === 'TRY' && supplierCurrency === 'USD') {
                balanceRevertImpact = selectedInvoice.total / exchangeRate;
            }

            const currentBalance = supplierData.balance || 0;
            batch.update(supplierRef, { balance: currentBalance + balanceRevertImpact });
        }

        try {
            await batch.commit();
            setSelectedInvoice(null);
        } catch (error) {
            console.error("Fatura silinirken hata:", error);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Bilinmiyor';
        // Handle Firestore Timestamp
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString('tr-TR');
        }
        // Handle JS Date or ISO String
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'Hatalı Tarih';
        return d.toLocaleDateString('tr-TR');
    };

    const sortByDate = (items, dateField = 'date') => {
        return [...items].sort((a, b) => {
            const dateA = a[dateField]?.seconds ? a[dateField].seconds * 1000 : new Date(a[dateField]).getTime();
            const dateB = b[dateField]?.seconds ? b[dateField].seconds * 1000 : new Date(b[dateField]).getTime();
            return dateB - dateA;
        });
    };

    if (loading) return <p>Yükleniyor...</p>;
    if (!supplier) return <h2>Tedarikçi Bulunamadı</h2>;

    const balanceStyle = supplier.balance >= 0 ? styles.balancePositive : styles.balanceNegative;

    const scrollbarCSS = `
        div::-webkit-scrollbar { width: 8px; }
        div::-webkit-scrollbar-track { background: #f1f5f9; }
        div::-webkit-scrollbar-thumb { background: #cbd5e1; borderRadius: 4px; }
        div::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
    `;

    return (
        <div style={{ ...styles.container, padding: '20px' }}>
            <style>{scrollbarCSS}</style>
            
            {/* Header */}
            <div style={styles.header}>
                <Link to="/tedarikciler" style={styles.backLink}>← Tedarikçiler</Link>
                <div>
                    {isEditing ? (
                        <button onClick={handleSave} style={{ ...styles.button, background: '#198754', marginRight: '10px' }}>Kaydet</button>
                    ) : (
                        <>
                            <button onClick={() => setShowReconciliationModal(true)} style={{ ...styles.button, background: '#6c757d', marginRight: '10px' }} title="Mutabakat Belgesi (PDF)">📋 Mutabakat</button>
                            <button onClick={() => setIsEditing(true)} style={{ ...styles.button, marginRight: '10px' }}>Düzenle</button>
                        </>
                    )}
                    <button onClick={handleDelete} style={{ ...styles.button, background: '#dc3545' }}>Sil</button>
                </div>
            </div>

            <div style={styles.grid}>
                <div style={styles.box}>
                    <h2 style={styles.title}>Tedarikçi Bilgileri</h2>
                    <ul style={styles.infoList}>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Ad</span>
                            <span style={styles.infoValue}>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="name"
                                        value={editedSupplier.name}
                                        onChange={handleEditChange}
                                        style={styles.input}
                                    />
                                ) : supplier.name}
                            </span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>E-posta</span>
                            <span style={styles.infoValue}>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        name="email"
                                        value={editedSupplier.email}
                                        onChange={handleEditChange}
                                        style={styles.input}
                                    />
                                ) : supplier.email || '-'}
                            </span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Telefon</span>
                            <span style={styles.infoValue}>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="phone"
                                        value={editedSupplier.phone}
                                        onChange={handleEditChange}
                                        style={styles.input}
                                    />
                                ) : supplier.phone || '-'}
                            </span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Adres</span>
                            <span style={styles.infoValue}>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="address"
                                        value={editedSupplier.address}
                                        onChange={handleEditChange}
                                        style={styles.input}
                                    />
                                ) : supplier.address || '-'}
                            </span>
                        </li>
                        <li style={{ ...styles.infoItem, padding: '20px 0' }}>
                            <span style={styles.infoLabel}>Bakiye Döviz Türü</span>
                            <span style={styles.infoValue}>
                                {isEditing ? (
                                    <select
                                        name="currency"
                                        value={editedSupplier.currency || 'TRY'}
                                        onChange={handleEditChange}
                                        style={styles.select}
                                    >
                                        <option value="TRY">TL (₺)</option>
                                        <option value="USD">USD ($)</option>
                                    </select>
                                ) : (supplier.currency === 'USD' ? 'USD ($)' : 'TL (₺)')}
                            </span>
                        </li>
                        <li style={{ ...styles.infoItem, padding: '20px 0' }}>
                            <span style={styles.infoLabel}>Bakiye</span>
                            <span style={{ ...styles.infoValue, ...balanceStyle, fontSize: '20px', fontWeight: 'bold' }}>
                                {(supplier.balance || 0).toFixed(2)} {supplier.currency === 'USD' ? '$' : '₺'}
                            </span>
                        </li>
                    </ul>
                </div>

                <div>
                    <div style={styles.box}>

                        {/* Confectionery Costs Section */}
                        <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Konfeksiyon Üretim Maliyetleri</h3>
                        {confectioneryCosts.length > 0 ? (
                            <div>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Sipariş ID</th>
                                            <th style={styles.th}>Ürün</th>
                                            <th style={styles.th}>Miktar</th>
                                            <th style={styles.th}>Maliyet</th>
                                            <th style={styles.th}>Tarih</th>
                                            <th style={styles.th}>Durum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {confectioneryCosts.map((cost) => (
                                            <tr key={cost.id} style={styles.trHover}>
                                                <td style={styles.td}>{cost.orderId.substring(0, 8).toUpperCase()}</td>
                                                <td style={styles.td}>{cost.product}</td>
                                                <td style={styles.td}>{cost.quantity} {cost.unit}</td>
                                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#dc3545' }}>
                                                    {cost.cost.toFixed(2)} ₺
                                                </td>
                                                <td style={styles.td}>{formatDate(cost.date)}</td>
                                                <td style={styles.td}>
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        backgroundColor: cost.status === 'Tamamlandı' ? '#d4edda' : '#f8d7da',
                                                        color: cost.status === 'Tamamlandı' ? '#155724' : '#721c24'
                                                    }}>
                                                        {cost.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                                    <strong>Toplam Konfeksiyon Maliyeti: </strong>
                                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                        {confectioneryCosts.reduce((sum, cost) => sum + cost.cost, 0).toFixed(2)} ₺
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                                Bu tedarikçi için henüz konfeksiyon üretim maliyeti kaydedilmemiş.
                            </p>
                        )}

                        <h3 style={{ ...styles.title, fontSize: '20px', marginTop: '30px', marginBottom: '20px' }}>Hammadde Alımları</h3>
                        {rawMaterialPurchases.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>ID</th>
                                        <th style={styles.th}>Tarih</th>
                                        <th style={styles.th}>Hammadde</th>
                                        <th style={styles.th}>Toplam</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortByDate(rawMaterialPurchases, 'purchaseDate').map(purchase => (
                                        <tr key={purchase.id}>
                                            <td style={styles.td}>{purchase.id.substring(0, 8).toUpperCase()}</td>
                                            <td style={styles.td}>{formatDate(purchase.purchaseDate)}</td>
                                            <td style={styles.td}>
                                                {purchase.lineItems?.map(item => `${item.productName} (${item.quantity} ${item.unit})`).join(', ') || '-'}
                                            </td>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                {(purchase.total || 0).toFixed(2)} {purchase.currency === 'USD' ? '$' : '₺'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Henüz hammadde alımı kaydedilmemiş.</p>
                        )}

                        <h3 style={{ ...styles.title, fontSize: '20px', marginTop: '30px', marginBottom: '20px' }}>Alış Faturaları</h3>
                        {purchaseInvoices.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Fatura No</th>
                                        <th style={styles.th}>Tarih</th>
                                        <th style={styles.th}>Toplam</th>
                                        <th style={styles.th}>Durum</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortByDate(purchaseInvoices, 'invoiceDate').map(invoice => (
                                        <tr
                                            key={invoice.id}
                                            style={styles.trHover}
                                            onClick={() => setSelectedInvoice(invoice)}
                                        >
                                            <td style={styles.td}>{invoice.invoiceNumber || invoice.id.substring(0, 8).toUpperCase()}</td>
                                            <td style={styles.td}>{formatDate(invoice.invoiceDate)}</td>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                {(invoice.total || 0).toFixed(2)} {invoice.currency === 'USD' ? '$' : '₺'}
                                            </td>
                                            <td style={styles.td}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    backgroundColor: invoice.status === 'Ödendi' ? '#d4edda' : '#f8d7da',
                                                    color: invoice.status === 'Ödendi' ? '#155724' : '#721c24'
                                                }}>
                                                    {invoice.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Henüz alış faturası oluşturulmamış.</p>
                        )}

                        <h3 style={{ ...styles.title, fontSize: '20px', marginTop: '30px', marginBottom: '20px' }}>Ödemeler</h3>
                        {payments.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Tarih</th>
                                        <th style={styles.th}>Açıklama</th>
                                        <th style={styles.th}>Tutar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortByDate(payments, 'date').map(payment => (
                                        <tr key={payment.id} style={styles.trHover}>
                                            <td style={styles.td}>{formatDate(payment.date)}</td>
                                            <td style={styles.td}>{payment.description || '-'}</td>
                                            <td style={{ ...styles.td, fontWeight: 'bold', color: '#198754' }}>
                                                +{(payment.amount || 0).toFixed(2)} {payment.currency === 'USD' ? '$' : '₺'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Henüz ödeme kaydedilmemiş.</p>
                        )}
                    </div>
                </div>
            </div>

            {selectedInvoice && (
                <PurchaseInvoiceDetailModal
                    invoice={selectedInvoice}
                    products={products}
                    onClose={() => setSelectedInvoice(null)}
                    onSave={handleSaveInvoice}
                    onDelete={handleDeleteInvoice}
                />
            )}

            {showReconciliationModal && (
                <ReconciliationModal 
                    onClose={() => setShowReconciliationModal(false)}
                    onConfirm={handleGenerateReconciliation}
                    lastDate={supplier.lastReconciliationDate}
                />
            )}
        </div>
    );
}

export default SupplierDetail;